// The user's licenses, and the installations others have let them into. Issuing a license ends in
// the one screen where both keys are shown together with where to put them.
import { useEffect, useState, type FormEvent } from 'react'
import { errorMessage, formatDate, portal, type AccessInfo, type CreateLicenseResult, type LicenseSummary, type TemplateInfo } from '../api'
import { Badge, Card, Copyable, EmptyState, ErrorText, Field, Icon, Link, PageHeader, Snippet, Spinner, Steps } from '../components/Ui'
import { navigate } from '../router'

export function LicensesPage() {
  const [licenses, setLicenses] = useState<LicenseSummary[] | null>(null)
  const [access, setAccess] = useState<AccessInfo[] | null>(null)
  const [templates, setTemplates] = useState<TemplateInfo[]>([])
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreateLicenseResult | null>(null)

  async function load() {
    try {
      const [l, a, t] = await Promise.all([portal.licenses(), portal.access(), portal.templates()])
      setLicenses(l)
      setAccess(a)
      setTemplates(t)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const active = licenses?.filter((l) => !l.disabled && !l.expired).length ?? 0

  return (
    <>
      <PageHeader
        title="Your licenses"
        lead="A license is what a Relatude.DB installation runs under. It carries the product's features and limits, holds the API keys an installation identifies itself with, and lists the installations that have reported in."
        actions={
          !creating &&
          !created && (
            <button type="button" className="button button-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={16} />
              New license
            </button>
          )
        }
      />
      <ErrorText error={error} />

      {created && (
        <Card title="License created" actions={<Badge tone="good">Ready to use</Badge>}>
          <p>
            Put these two keys into the Relatude.DB installation this license is for: in its <code>relatude.db.json</code> as below, or on the Security page
            of its admin UI. Both stay visible on the license page, where you can also make more API keys later.
          </p>
          <div className="grid-2">
            <Snippet
              title="relatude.db.json"
              lines={[
                { key: 'LicenseKey', value: created.licenseId },
                { key: 'ApiKey', value: created.apiKey },
              ]}
            />
            <div className="key-list">
              <Copyable label="License key" value={created.licenseId} />
              <Copyable label="API key" value={created.apiKey} />
              <p className="muted small">
                The license key names the license and is not secret. The API key is: anything that holds it can report in as this license, so keep it
                with the installation's configuration.
              </p>
            </div>
          </div>
          <div className="row">
            <button type="button" className="button button-primary" onClick={() => navigate(`/licenses/${created.licenseId}`)}>
              Open the license
            </button>
            <button
              type="button"
              className="button button-quiet"
              onClick={() => {
                setCreated(null)
                void load()
              }}
            >
              Done
            </button>
          </div>
        </Card>
      )}

      {creating && (
        <NewLicense
          templates={templates}
          onCancel={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false)
            setCreated(result)
            void load()
          }}
        />
      )}

      {licenses === null ? (
        <Spinner label="Loading your licenses…" />
      ) : licenses.length === 0 ? (
        !creating && !created && <GettingStarted onCreate={() => setCreating(true)} />
      ) : (
        <Card
          title="Licenses"
          lead={`${licenses.length} ${licenses.length === 1 ? 'license' : 'licenses'}, ${active} active. Open one for its keys, entitlements and installations.`}
        >
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th className="num">API keys</th>
                  <th className="num">Installations</th>
                  <th className="chev" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {licenses.map((l) => (
                  <tr key={l.id} className="clickable" onClick={() => navigate(`/licenses/${l.id}`)}>
                    <td>
                      <Link to={`/licenses/${l.id}`} className="table-title">
                        {l.name}
                      </Link>
                      <div className="muted small">
                        <code>{l.id}</code>
                      </div>
                    </td>
                    <td>
                      {l.disabled ? <Badge tone="bad">Disabled</Badge> : l.expired ? <Badge tone="bad">Expired</Badge> : <Badge tone="good">Active</Badge>}
                    </td>
                    <td>{l.expiresUtc ? formatDate(l.expiresUtc) : 'Never'}</td>
                    <td className="num">{l.apiKeys}</td>
                    <td className="num">{l.installations}</td>
                    <td className="chev">
                      <Icon name="chevron-right" size={16} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {access && access.length > 0 && (
        <Card
          title="Installations you may sign in to"
          lead="These run under other people's licenses, and their owners have granted you access. Sign in from each installation's own admin UI with “Sign in with Relatude.License”; there is nothing to do here."
        >
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Installation</th>
                  <th>License</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {access.map((a) => (
                  <tr key={a.installationId}>
                    <td>
                      <span className="table-title">{a.installationName}</span>
                      <div className="muted small">
                        <code>{a.installationKey}</code>
                      </div>
                    </td>
                    <td>{a.licenseName}</td>
                    <td>{a.ownerName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}

/** What a user with no license yet sees: what a license is, and the three steps to a running installation. */
function GettingStarted({ onCreate }: { onCreate: () => void }) {
  return (
    <Card>
      <EmptyState
        icon="key"
        title="No licenses yet"
        action={
          <button type="button" className="button button-primary" onClick={onCreate}>
            <Icon name="plus" size={16} />
            Create your first license
          </button>
        }
      >
        A license is what a Relatude.DB installation runs under. Getting one running takes three steps.
      </EmptyState>
      <Steps
        steps={[
          {
            title: 'Create a license',
            text: 'Pick a product bundle and name the license after the site or project it is for. The license key and a first API key are shown the moment it exists.',
          },
          {
            title: 'Give the keys to the installation',
            text: (
              <>
                Put them in <code>relatude.db.json</code> as <code>LicenseKey</code> and <code>ApiKey</code>, or on the Security page of the admin UI. The
                installation reports in and appears on the license page.
              </>
            ),
          },
          {
            title: 'Approve sign-in',
            text: 'Approve the installation and the address its admin UI is opened on. From then on you sign in there with this account, and can grant others the same.',
          },
        ]}
      />
    </Card>
  )
}

function NewLicense({ templates, onCancel, onCreated }: { templates: TemplateInfo[]; onCancel: () => void; onCreated: (result: CreateLicenseResult) => void }) {
  const [templateId, setTemplateId] = useState<string>(templates[0]?.id ?? '')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const template = templates.find((t) => t.id === templateId) ?? null

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onCreated(await portal.createLicense(templateId || null, name))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="New license" lead="Choose what the license entitles an installation to, and give it a name. The keys are shown as soon as it exists.">
      <form onSubmit={submit} className="form">
        <div className="grid-2">
          <div className="form">
            <Field
              label="Product bundle"
              hint={templates.length === 0 ? 'No product bundles are offered yet; the license starts without entitlements.' : 'The features, limits and credits the license starts with.'}
            >
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
                <option value="">No product bundle (bare license)</option>
              </select>
            </Field>
            <Field label="Name" hint="For you: the site or project this license is for. You can rename it later.">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={template?.name ?? 'My site'} />
            </Field>
          </div>
          <div className="bundle">
            <h3>What the license carries</h3>
            {!template ? (
              <p className="muted small">A bare license: no features, limits or credits until they are added on the server.</p>
            ) : (
              <div className="entitlements">
                {template.features.length > 0 && (
                  <div>
                    <div className="muted small">Features</div>
                    <div className="chips">
                      {template.features.map((f) => (
                        <span key={f} className="chip">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {template.limits.length > 0 && (
                  <div>
                    <div className="muted small">Limits</div>
                    <div>{template.limits.map((l) => `${l.name} ${l.unlimited ? 'unlimited' : l.maxValue.toLocaleString()}`).join(' · ')}</div>
                  </div>
                )}
                {template.accounts.length > 0 && (
                  <div>
                    <div className="muted small">Monthly credits</div>
                    <div>{template.accounts.map((a) => `${a.name} ${a.monthlyLimit.toLocaleString()}`).join(' · ')}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <ErrorText error={error} />
        <div className="row">
          <button className="button button-primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create license'}
          </button>
          <button type="button" className="button button-quiet" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  )
}
